import React, { useState, useEffect } from 'react';
import seasonService from '../services/seasonService';
import firebaseAuthService from '../services/firebaseAuthService';
import translationService from '../services/translationService';

const SeasonManager = () => {
  const [currentSession, setCurrentSession] = useState(null);
  const [seasonData, setSeasonData] = useState(null);
  const [allSeasons, setAllSeasons] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [rotationRecommendations, setRotationRecommendations] = useState([]);
  const [comparisonSeasons, setComparisonSeasons] = useState({ season1: '', season2: '' });
  const [comparisonData, setComparisonData] = useState(null);
  const [closingNotes, setClosingNotes] = useState('');
  const [showCloseDialog, setShowCloseDialog] = useState(false);
  const [showStartDialog, setShowStartDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [costAnalysis, setCostAnalysis] = useState(null);
  const [newSeasonData, setNewSeasonData] = useState({
    startDate: new Date().toISOString().split('T')[0],
    endDate: new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().split('T')[0],
    notes: ''
  });

  // Fix translation service usage
  const t = (key) => {
    try {
      return translationService.translate(key) || key;
    } catch (error) {
      console.warn('Translation error:', error);
      return key;
    }
  };

  useEffect(() => {
    const session = firebaseAuthService.getCurrentSession();
    if (session?.farm?.id) {
      setCurrentSession(session);
      loadSeasonData(session.farm.id);
    }
    
    // Listen for cloud sync completion - FORCE RELOAD
    const handleCloudSyncComplete = (event) => {
      console.log('📱 Cloud sync completed - FORCE RELOADING SeasonManager data');
      setTimeout(() => {
        const session = firebaseAuthService.getCurrentSession();
        if (session?.farm?.id) {
          loadSeasonData(session.farm.id);
        }
      }, 500); // Small delay to ensure data is written
    };
    
    window.addEventListener('cloudSyncComplete', handleCloudSyncComplete);
    window.addEventListener('seasonDataUpdated', handleCloudSyncComplete);
    
    return () => {
      window.removeEventListener('cloudSyncComplete', handleCloudSyncComplete);
      window.removeEventListener('seasonDataUpdated', handleCloudSyncComplete);
    };
  }, []);

  const loadSeasonData = (farmId) => {
    try {
      const currentSeasonData = seasonService.getCurrentSeasonData(farmId);
      const seasons = seasonService.getAllSeasons(farmId);
      const recommendations = seasonService.getCropRotationRecommendations(farmId);
      const analysis = calculateCostProfitAnalysis(currentSeasonData, farmId);

      setSeasonData(currentSeasonData);
      setAllSeasons(seasons);
      setRotationRecommendations(recommendations);
      setCostAnalysis(analysis);
    } catch (error) {
      console.error('Error loading season data:', error);
    }
  };

  const calculateCostProfitAnalysis = (seasonData, farmId) => {
    console.log('🧮 Calculating cost analysis for season:', seasonData);
    
    try {
      // Get current session for proper farm data
      const currentSession = firebaseAuthService.getCurrentSession();
      if (!currentSession?.farm?.id) {
        console.log('❌ No current session found');
        return null;
      }

      // Get real inventory data from the correct source
      const inventoryKey = `agrimanage-inventory_${farmId}`;
      const inventoryData = JSON.parse(localStorage.getItem(inventoryKey) || '{}');
      const materials = inventoryData.data?.materials || [];
      const equipment = inventoryData.data?.equipment || [];
      
      console.log('📦 Inventory materials found:', materials.length);
      console.log('🚜 Equipment found:', equipment.length);
      
      // Get parcel data for area calculation from the correct source
      const parcelKey = `parcelsData_${farmId}`;
      const parcelData = JSON.parse(localStorage.getItem(parcelKey) || '[]');
      const totalArea = parcelData.reduce((sum, parcel) => sum + (parseFloat(parcel.suprafata) || 0), 0);
      
      console.log('🌾 Total area from parcels:', totalArea, 'ha');
      
      // Get StockAdjustment data for real costs
      const stockKey = `stockData_${farmId}`;
      const stockData = JSON.parse(localStorage.getItem(stockKey) || '{}');
      const invoices = stockData.data?.invoices || [];
      
      console.log('📋 Invoices found:', invoices.length);
      
      // Calculate real costs from invoices
      let totalInvoiceCosts = 0;
      let materialCosts = 0;
      let equipmentCosts = 0;
      let fuelCosts = 0;
      
      invoices.forEach(invoice => {
        if (invoice.items && Array.isArray(invoice.items)) {
          invoice.items.forEach(item => {
            const itemCost = (parseFloat(item.quantity) || 0) * (parseFloat(item.unitPrice) || 0);
            totalInvoiceCosts += itemCost;
            
            // Categorize costs
            if (item.productType === 'Motorină') {
              fuelCosts += itemCost;
            } else if (['Îngrășăminte', 'Pesticide', 'Semințe'].includes(item.productType)) {
              materialCosts += itemCost;
            } else {
              equipmentCosts += itemCost;
            }
          });
        }
      });
      
      // Add inventory costs if no invoice data
      if (totalInvoiceCosts === 0) {
        materialCosts = materials.reduce((sum, material) => {
          const quantity = parseFloat(material.quantity) || 0;
          const unitPrice = parseFloat(material.unitPrice) || 0;
          return sum + (quantity * unitPrice);
        }, 0);
        
        equipmentCosts = equipment.reduce((sum, equip) => {
          const depreciation = parseFloat(equip.depreciation) || 0;
          const fuelCost = parseFloat(equip.fuelCost) || 0;
          return sum + depreciation + fuelCost;
        }, 0);
        
        totalInvoiceCosts = materialCosts + equipmentCosts;
      }
      
      // Get financial data (revenue)
      const financialKey = `agrimanage-finances_${farmId}`;
      const financialData = JSON.parse(localStorage.getItem(financialKey) || '{}');
      const revenue = parseFloat(financialData.totalRevenue) || 0;
      
      const totalCosts = totalInvoiceCosts;
      const profit = revenue - totalCosts;
      
      const result = {
        totalArea: totalArea,
        revenue: revenue,
        totalCosts: totalCosts,
        materialCosts: materialCosts,
        equipmentCosts: equipmentCosts,
        fuelCosts: fuelCosts,
        profit: profit,
        costPerHa: totalArea > 0 ? (totalCosts / totalArea) : 0,
        profitPerHa: totalArea > 0 ? (profit / totalArea) : 0,
        profitMargin: revenue > 0 ? ((profit / revenue) * 100) : 0,
        invoiceCount: invoices.length
      };
      
      console.log('📊 Cost analysis result:', result);
      return result;
      
    } catch (error) {
      console.error('❌ Error calculating cost analysis:', error);
      return null;
    }
  };

  const handleCloseSeason = async () => {
    if (!currentSession?.farm?.id) return;

    setLoading(true);
    try {
      const result = await seasonService.closeSeasonAndStartNew(
        currentSession.farm.id,
        closingNotes
      );

      if (result.success) {
        alert(`✅ ${result.message}`);
        loadSeasonData(currentSession.farm.id);
        setShowCloseDialog(false);
        setClosingNotes('');
      } else {
        alert(`❌ Error: ${result.error}`);
      }
    } catch (error) {
      alert(`❌ Error closing season: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleStartNewSeason = async () => {
    if (!currentSession?.farm?.id) return;

    setLoading(true);
    try {
      // Create new season manually
      const newSeason = {
        year: new Date(newSeasonData.startDate).getFullYear(),
        startDate: newSeasonData.startDate,
        endDate: newSeasonData.endDate,
        status: 'active',
        notes: newSeasonData.notes,
        data: {
          fields: [],
          tasks: [],
          parcels: [],
          inventory: { materials: [], equipment: [] },
          finances: { revenue: 0, expenses: 0 }
        }
      };

      // Save to localStorage (in real app, this would be Firebase)
      const existingSeasons = JSON.parse(localStorage.getItem('agrimanage-seasons') || '[]');
      
      // Close current season if active
      const updatedSeasons = existingSeasons.map(season => 
        season.status === 'active' ? { ...season, status: 'closed', endDate: new Date().toISOString() } : season
      );
      
      // Add new season
      updatedSeasons.push(newSeason);
      localStorage.setItem('agrimanage-seasons', JSON.stringify(updatedSeasons));

      alert(`✅ Sezonul ${newSeason.year} a fost creat cu succes!`);
      loadSeasonData(currentSession.farm.id);
      setShowStartDialog(false);
      setNewSeasonData({
        startDate: new Date().toISOString().split('T')[0],
        endDate: new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().split('T')[0],
        notes: ''
      });
    } catch (error) {
      alert(`❌ Eroare la crearea sezonului: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSeasonComparison = () => {
    if (!currentSession?.farm?.id || !comparisonSeasons.season1 || !comparisonSeasons.season2) {
      return;
    }

    const comparison = seasonService.getSeasonComparison(
      currentSession.farm.id,
      parseInt(comparisonSeasons.season1),
      parseInt(comparisonSeasons.season2)
    );

    setComparisonData(comparison);
  };

  const canCloseSeason = () => {
    if (!currentSession?.farm?.id) return { canClose: false, reason: 'No farm selected' };
    return seasonService.canCloseSeason(currentSession.farm.id);
  };

  const renderOverview = () => {
    if (!seasonData) return <div>Loading season data...</div>;

    const closeStatus = canCloseSeason();
    const isApproaching = seasonService.isApproachingSeasonEnd();

    return (
      <div className="season-overview">
        <div className="season-header">
          <h2 className="text-2xl font-bold text-green-700 mb-4">
            {t('currentSeason')}: {seasonData.year}
          </h2>
          <div className="season-status">
            <span className={`status-badge ${seasonData.status === 'active' ? 'active' : 'closed'}`}>
              {t(seasonData.status)}
            </span>
            {isApproaching && (
              <span className="warning-badge">
                ⚠️ {t('seasonEndingWarning')}
              </span>
            )}
          </div>
        </div>

        <div className="season-stats grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div className="stat-card bg-blue-50 p-4 rounded-lg">
            <h3 className="text-lg font-semibold text-blue-700">{t('fields')}</h3>
            <p className="text-2xl font-bold text-blue-900">
              {seasonData.data.fields?.length || 0}
            </p>
            <p className="text-sm text-blue-600">
              {seasonService.calculateTotalArea(seasonData.data.fields || [])} ha
            </p>
          </div>

          <div className="stat-card bg-green-50 p-4 rounded-lg">
            <h3 className="text-lg font-semibold text-green-700">{t('tasks')}</h3>
            <p className="text-2xl font-bold text-green-900">
              {seasonData.data.tasks?.length || 0}
            </p>
            <p className="text-sm text-green-600">
              {seasonData.data.tasks?.filter(t => t.status === 'completed').length || 0} {t('completed')}
            </p>
          </div>

          <div className="stat-card bg-yellow-50 p-4 rounded-lg">
            <h3 className="text-lg font-semibold text-yellow-700">{t('parcels')}</h3>
            <p className="text-2xl font-bold text-yellow-900">
              {seasonData.data.parcels?.length || 0}
            </p>
            <p className="text-sm text-yellow-600">{t('activeContracts')}</p>
          </div>
        </div>

        <div className="season-dates bg-gray-50 p-4 rounded-lg mb-6">
          <h3 className="text-lg font-semibold mb-2">{t('seasonDates')}</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <span className="text-sm text-gray-600">{t('startDate')}:</span>
              <p className="font-medium">
                {new Date(seasonData.startDate).toLocaleDateString('ro-RO')}
              </p>
            </div>
            <div>
              <span className="text-sm text-gray-600">{t('endDate')}:</span>
              <p className="font-medium">
                {new Date(seasonData.endDate).toLocaleDateString('ro-RO')}
              </p>
            </div>
          </div>
        </div>

        {/* Cost/Profit Analysis */}
        {costAnalysis && (
          <div className="cost-analysis bg-gradient-to-r from-green-50 to-blue-50 p-6 rounded-lg mb-6">
            <h3 className="text-xl font-semibold mb-4 text-green-800">📊 Analiza Costuri/Profit</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div className="bg-white p-4 rounded-lg shadow">
                <h4 className="text-sm font-medium text-gray-600">Suprafață Totală</h4>
                <p className="text-2xl font-bold text-blue-600">{costAnalysis.totalArea.toFixed(2)} ha</p>
              </div>
              <div className="bg-white p-4 rounded-lg shadow">
                <h4 className="text-sm font-medium text-gray-600">Venituri Totale</h4>
                <p className="text-2xl font-bold text-green-600">{costAnalysis.revenue.toFixed(2)} LEI</p>
              </div>
              <div className="bg-white p-4 rounded-lg shadow">
                <h4 className="text-sm font-medium text-gray-600">Costuri Totale</h4>
                <p className="text-2xl font-bold text-red-600">{costAnalysis.totalCosts.toFixed(2)} LEI</p>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-white p-4 rounded-lg shadow">
                <h4 className="text-sm font-medium text-gray-600">💰 Profit Total</h4>
                <p className={`text-xl font-bold ${costAnalysis.profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {costAnalysis.profit.toFixed(2)} LEI
                </p>
              </div>
              <div className="bg-white p-4 rounded-lg shadow">
                <h4 className="text-sm font-medium text-gray-600">📊 Cost per Ha</h4>
                <p className="text-xl font-bold text-orange-600">{costAnalysis.costPerHa.toFixed(2)} LEI/ha</p>
                <p className="text-xs text-gray-500">Din {costAnalysis.invoiceCount} facturi</p>
              </div>
              <div className="bg-white p-4 rounded-lg shadow">
                <h4 className="text-sm font-medium text-gray-600">🌾 Profit per Ha</h4>
                <p className={`text-xl font-bold ${costAnalysis.profitPerHa >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {costAnalysis.profitPerHa.toFixed(2)} LEI/ha
                </p>
              </div>
              <div className="bg-white p-4 rounded-lg shadow">
                <h4 className="text-sm font-medium text-gray-600">📈 Marjă Profit</h4>
                <p className={`text-xl font-bold ${costAnalysis.profitMargin >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {costAnalysis.profitMargin.toFixed(1)}%
                </p>
              </div>
            </div>
            
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-white p-4 rounded-lg shadow">
                <h4 className="text-sm font-medium text-gray-600 mb-2">💳 Breakdown Costuri</h4>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span>🌱 Materiale (semințe, pesticide, îngrășăminte):</span>
                    <span className="font-medium">{costAnalysis.materialCosts.toFixed(2)} LEI</span>
                  </div>
                  <div className="flex justify-between">
                    <span>⛽ Motorină:</span>
                    <span className="font-medium">{costAnalysis.fuelCosts.toFixed(2)} LEI</span>
                  </div>
                  <div className="flex justify-between">
                    <span>🚜 Echipamente:</span>
                    <span className="font-medium">{costAnalysis.equipmentCosts.toFixed(2)} LEI</span>
                  </div>
                  <div className="flex justify-between border-t pt-2 font-bold">
                    <span>📊 TOTAL:</span>
                    <span>{costAnalysis.totalCosts.toFixed(2)} LEI</span>
                  </div>
                </div>
              </div>
              <div className="bg-white p-4 rounded-lg shadow">
                <h4 className="text-sm font-medium text-gray-600 mb-2">Performanță</h4>
                <div className="space-y-2">
                  <div className={`text-sm ${costAnalysis.profitMargin >= 20 ? 'text-green-600' : costAnalysis.profitMargin >= 10 ? 'text-yellow-600' : 'text-red-600'}`}>
                    {costAnalysis.profitMargin >= 20 ? '✅ Performanță Excelentă' : 
                     costAnalysis.profitMargin >= 10 ? '⚠️ Performanță Medie' : 
                     '❌ Performanță Slabă'}
                  </div>
                  <div className="text-xs text-gray-500">
                    Marjă optimă: 15-25%
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Season Management Actions */}
        <div className="season-actions bg-blue-50 p-4 rounded-lg mb-6">
          <h3 className="text-lg font-semibold text-blue-700 mb-4">🔧 Acțiuni Sezon</h3>
          <div className="flex gap-3 flex-wrap">
            <button
              onClick={() => setShowStartDialog(true)}
              className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 flex items-center gap-2"
            >
              <span>🌱</span> Începe Sezon Nou
            </button>
            
            {closeStatus.canClose && (
              <button
                onClick={() => setShowCloseDialog(true)}
                className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 flex items-center gap-2"
              >
                <span>🏁</span> Închide Sezonul Curent
              </button>
            )}
            
            <button
              onClick={() => loadSeasonData(currentSession?.farm?.id)}
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 flex items-center gap-2"
            >
              <span>🔄</span> Reîmprospătează Date
            </button>
          </div>
        </div>

        {closeStatus.canClose && (
          <div className="close-season-section bg-red-50 p-4 rounded-lg">
            <h3 className="text-lg font-semibold text-red-700 mb-2">
              {t('closeSeasonTitle')}
            </h3>
            <p className="text-red-600 mb-4">{t('closeSeasonDescription')}</p>
            <button
              onClick={() => setShowCloseDialog(true)}
              className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
            >
              {t('closeSeason')}
            </button>
          </div>
        )}
      </div>
    );
  };

  const renderRotationRecommendations = () => {
    if (rotationRecommendations.length === 0) {
      return (
        <div className="text-center py-8">
          <p className="text-gray-500">{t('noRotationData')}</p>
        </div>
      );
    }

    return (
      <div className="rotation-recommendations">
        <h3 className="text-xl font-semibold mb-4">{t('cropRotationRecommendations')}</h3>
        <div className="space-y-4">
          {rotationRecommendations.map((rec, index) => (
            <div key={index} className="recommendation-card bg-white p-4 rounded-lg border">
              <div className="flex justify-between items-start mb-2">
                <h4 className="font-semibold text-lg">{rec.fieldName}</h4>
                <span className="text-sm bg-gray-100 px-2 py-1 rounded">
                  {t('previousCrop')}: {rec.previousCrop}
                </span>
              </div>
              
              <div className="recommended-crops mb-3">
                <p className="text-sm font-medium text-green-600 mb-1">
                  {t('recommendedCrops')}:
                </p>
                <div className="flex flex-wrap gap-2">
                  {rec.recommendedCrops.map((crop, idx) => (
                    <span key={idx} className="bg-green-100 text-green-800 px-2 py-1 rounded text-sm">
                      {crop}
                    </span>
                  ))}
                </div>
              </div>

              <div className="reason mb-3">
                <p className="text-sm font-medium text-blue-600 mb-1">{t('reason')}:</p>
                <p className="text-sm text-gray-700">{rec.reason}</p>
              </div>

              <div className="benefits">
                <p className="text-sm font-medium text-purple-600 mb-1">{t('benefits')}:</p>
                <ul className="text-sm text-gray-700 list-disc list-inside">
                  {rec.benefits.map((benefit, idx) => (
                    <li key={idx}>{benefit}</li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderComparison = () => {
    return (
      <div className="season-comparison">
        <h3 className="text-xl font-semibold mb-4">{t('seasonComparison')}</h3>
        
        <div className="comparison-controls bg-gray-50 p-4 rounded-lg mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
            <div>
              <label className="block text-sm font-medium mb-1">{t('season')} 1:</label>
              <select
                value={comparisonSeasons.season1}
                onChange={(e) => setComparisonSeasons(prev => ({ ...prev, season1: e.target.value }))}
                className="w-full p-2 border rounded"
              >
                <option value="">{t('selectSeason')}</option>
                {allSeasons.map(season => (
                  <option key={season.year} value={season.year}>
                    {season.year} ({t(season.status)})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">{t('season')} 2:</label>
              <select
                value={comparisonSeasons.season2}
                onChange={(e) => setComparisonSeasons(prev => ({ ...prev, season2: e.target.value }))}
                className="w-full p-2 border rounded"
              >
                <option value="">{t('selectSeason')}</option>
                {allSeasons.map(season => (
                  <option key={season.year} value={season.year}>
                    {season.year} ({t(season.status)})
                  </option>
                ))}
              </select>
            </div>

            <button
              onClick={handleSeasonComparison}
              disabled={!comparisonSeasons.season1 || !comparisonSeasons.season2}
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50"
            >
              {t('compare')}
            </button>
          </div>
        </div>

        {comparisonData && (
          <div className="comparison-results">
            <h4 className="text-lg font-semibold mb-4">
              {t('comparisonResults')}: {comparisonData.season1} vs {comparisonData.season2}
            </h4>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="comparison-card bg-blue-50 p-4 rounded-lg">
                <h5 className="font-semibold text-blue-700 mb-2">
                  {t('season')} {comparisonData.season1}
                </h5>
                <div className="space-y-2 text-sm">
                  <p>{t('fields')}: {comparisonData.comparison.fields.season1Count} ({comparisonData.comparison.fields.season1Area} ha)</p>
                  <p>{t('tasks')}: {comparisonData.comparison.tasks.season1Count} ({comparisonData.comparison.tasks.season1Completed} {t('completed')})</p>
                  <div>
                    <p className="font-medium">{t('crops')}:</p>
                    {Object.entries(comparisonData.comparison.crops.season1Crops).map(([crop, area]) => (
                      <span key={crop} className="inline-block bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs mr-1 mb-1">
                        {crop}: {area} ha
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              <div className="comparison-card bg-green-50 p-4 rounded-lg">
                <h5 className="font-semibold text-green-700 mb-2">
                  {t('season')} {comparisonData.season2}
                </h5>
                <div className="space-y-2 text-sm">
                  <p>{t('fields')}: {comparisonData.comparison.fields.season2Count} ({comparisonData.comparison.fields.season2Area} ha)</p>
                  <p>{t('tasks')}: {comparisonData.comparison.tasks.season2Count} ({comparisonData.comparison.tasks.season2Completed} {t('completed')})</p>
                  <div>
                    <p className="font-medium">{t('crops')}:</p>
                    {Object.entries(comparisonData.comparison.crops.season2Crops).map(([crop, area]) => (
                      <span key={crop} className="inline-block bg-green-100 text-green-800 px-2 py-1 rounded text-xs mr-1 mb-1">
                        {crop}: {area} ha
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderHistory = () => {
    return (
      <div className="season-history">
        <h3 className="text-xl font-semibold mb-4">{t('seasonHistory')}</h3>
        
        <div className="space-y-4">
          {allSeasons.map(season => (
            <div key={season.year} className="season-card bg-white p-4 rounded-lg border">
              <div className="flex justify-between items-start mb-2">
                <h4 className="font-semibold text-lg">{t('season')} {season.year}</h4>
                <span className={`status-badge ${season.status === 'active' ? 'active' : 'closed'}`}>
                  {t(season.status)}
                </span>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <span className="text-gray-600">{t('fields')}:</span>
                  <p className="font-medium">{season.data.fields?.length || 0}</p>
                </div>
                <div>
                  <span className="text-gray-600">{t('area')}:</span>
                  <p className="font-medium">{seasonService.calculateTotalArea(season.data.fields || [])} ha</p>
                </div>
                <div>
                  <span className="text-gray-600">{t('tasks')}:</span>
                  <p className="font-medium">{season.data.tasks?.length || 0}</p>
                </div>
                <div>
                  <span className="text-gray-600">{t('parcels')}:</span>
                  <p className="font-medium">{season.data.parcels?.length || 0}</p>
                </div>
              </div>

              {season.status === 'closed' && season.closedAt && (
                <div className="mt-3 pt-3 border-t">
                  <p className="text-sm text-gray-600">
                    {t('closedOn')}: {new Date(season.closedAt).toLocaleDateString('ro-RO')}
                  </p>
                  {season.closingNotes && (
                    <p className="text-sm text-gray-700 mt-1">
                      {t('notes')}: {season.closingNotes}
                    </p>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  if (!currentSession) {
    return <div className="text-center py-8">{t('pleaseLogin')}</div>;
  }

  return (
    <div className="season-manager p-6">
      <div className="header mb-6">
        <h1 className="text-3xl font-bold text-green-800 mb-2">{t('seasonManagement')}</h1>
        <p className="text-gray-600">{t('seasonManagementDescription')}</p>
      </div>

      <div className="tabs mb-6">
        <div className="tab-list flex space-x-1 bg-gray-100 p-1 rounded-lg">
          {['overview', 'rotation', 'comparison', 'history'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 rounded-md transition-colors ${
                activeTab === tab
                  ? 'bg-white text-green-700 shadow'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              {t(tab)}
            </button>
          ))}
        </div>
      </div>

      <div className="tab-content">
        {activeTab === 'overview' && renderOverview()}
        {activeTab === 'rotation' && renderRotationRecommendations()}
        {activeTab === 'comparison' && renderComparison()}
        {activeTab === 'history' && renderHistory()}
      </div>

      {/* Close Season Dialog */}
      {showCloseDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">{t('confirmCloseSeason')}</h3>
            <p className="text-gray-600 mb-4">{t('closeSeasonConfirmation')}</p>
            
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">{t('closingNotes')} ({t('optional')}):</label>
              <textarea
                value={closingNotes}
                onChange={(e) => setClosingNotes(e.target.value)}
                className="w-full p-2 border rounded-md"
                rows="3"
                placeholder={t('addClosingNotes')}
              />
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowCloseDialog(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
                disabled={loading}
              >
                {t('cancel')}
              </button>
              <button
                onClick={handleCloseSeason}
                className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 disabled:opacity-50"
                disabled={loading}
              >
                {loading ? t('closing') : t('closeSeason')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Start New Season Dialog */}
      {showStartDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">🌱 Începe Sezon Nou</h3>
            <p className="text-gray-600 mb-4">Configurează un nou sezon agricol</p>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Data Început *:</label>
                <input
                  type="date"
                  value={newSeasonData.startDate}
                  onChange={(e) => setNewSeasonData(prev => ({ ...prev, startDate: e.target.value }))}
                  className="w-full p-2 border rounded-md"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Data Sfârșit *:</label>
                <input
                  type="date"
                  value={newSeasonData.endDate}
                  onChange={(e) => setNewSeasonData(prev => ({ ...prev, endDate: e.target.value }))}
                  className="w-full p-2 border rounded-md"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Notițe (opțional):</label>
                <textarea
                  value={newSeasonData.notes}
                  onChange={(e) => setNewSeasonData(prev => ({ ...prev, notes: e.target.value }))}
                  className="w-full p-2 border rounded-md"
                  rows="3"
                  placeholder="Obiective, planuri, observații pentru acest sezon..."
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowStartDialog(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
                disabled={loading}
              >
                Anulează
              </button>
              <button
                onClick={handleStartNewSeason}
                className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 disabled:opacity-50"
                disabled={loading || !newSeasonData.startDate || !newSeasonData.endDate}
              >
                {loading ? 'Se creează...' : 'Creează Sezonul'}
              </button>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        .status-badge {
          padding: 4px 8px;
          border-radius: 12px;
          font-size: 12px;
          font-weight: 500;
        }
        .status-badge.active {
          background-color: #dcfce7;
          color: #166534;
        }
        .status-badge.closed {
          background-color: #fee2e2;
          color: #991b1b;
        }
        .warning-badge {
          background-color: #fef3c7;
          color: #92400e;
          padding: 4px 8px;
          border-radius: 12px;
          font-size: 12px;
          font-weight: 500;
        }
      `}</style>
    </div>
  );
};

export default SeasonManager; 